import { CloseOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';

import { Button, Col, DatePicker, Form, FormInstance, Input, Modal, Row, Select, Spin, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { IAppComponentProps } from '../../components';
import './styling/InvoiceStatusSearchForm.css';
import styles from '../info-portal/components/styling/infoportal.module.css';
import dayjs from 'dayjs';
import { EIMMonitoringGridRequest } from './interfaces';
import { getDateFormatOrDefault } from '../users/UserFormatPreferenceServices';
import { useQuery } from '@apollo/client';
import { QUERY_INVOICE_FILTERS } from './invotrackQueries';
import { removeFilterDuplicates } from './utils';
import {getRangePickerLocalization} from "../info-portal/components/grid/CalendarUtils";

type IProps = {
    searchFilters: EIMMonitoringGridRequest;
    form: FormInstance<any>;
    onSearch: any;
    onReset: any;
} & IAppComponentProps;

const SEARCH_IDENTIFIER_FIELDS = ['partnerno', 'reference', 'vin_number', 'message_id'];

const isDateRangeLongerThanThreeMonths = (fromDate?: dayjs.Dayjs, toDate?: dayjs.Dayjs) =>
    Boolean(fromDate && toDate && fromDate.isBefore(toDate.subtract(3, 'months')));

const InvoiceStatusSearchForm: React.FC<IProps> = (props) => {
    const { searchFilters, form, onSearch, onReset } = props;
    const intl = useIntl();
    const { Option } = Select;
    const formItemLayout = {
        labelCol: {
            span: 24,
        },
        wrapperCol: {
            span: 24,
        },
    };

    useEffect(()=> {
        //initialize the from and to date to today's date using dayjs
        form.setFieldsValue({
            fromDate: searchFilters.fromDate,
            toDate: searchFilters.toDate,
            process: window.sessionStorage.getItem('process')? JSON.parse(window.sessionStorage.getItem('process')) :null,
            company: window.sessionStorage.getItem('company')?JSON.parse(window.sessionStorage.getItem('company')) : null,
            group_name: window.sessionStorage.getItem('companyGroup')?JSON.parse(window.sessionStorage.getItem('companyGroup')) :undefined,
        });
    },[]);

    const { loading, data, error } = useQuery(QUERY_INVOICE_FILTERS, {
        variables: { optionQuery: 'all.countries.en' },
        fetchPolicy: 'network-only',
    });
    const filterOptions = data?.getAllMessagesFilters;

    const handleFromDateChange = (newFromDate:dayjs.Dayjs) => {
        if (newFromDate) {
            const toDate = form.getFieldValue('toDate');
            if (!toDate || newFromDate.isAfter(toDate)) {
                form.setFieldsValue({toDate: newFromDate.clone()});
            }
        }
        else {
            form.setFieldsValue({toDate: undefined});
        }
    }

    const handleToDate = (newToDate:dayjs.Dayjs)=> {
        if (newToDate) {
            const fromDate = form.getFieldValue('fromDate');
            if (fromDate) {
                if (newToDate.isBefore(fromDate)) {
                    form.setFieldsValue({fromDate: newToDate.clone()});
                }
            }
            else {
                form.setFieldsValue({fromDate: newToDate.clone()});
            }
        }
    };

    const handleFinish = (values: any) => {
        const updatedValues = { ...values };
        SEARCH_IDENTIFIER_FIELDS.forEach(field => {
            if (typeof updatedValues[field] === 'string') {
                updatedValues[field] = updatedValues[field].trim()
                    ? updatedValues[field].trim().split(/[\s+,]/).filter(Boolean)
                    : [];
            }
        });

        const hasSearchIdentifier = SEARCH_IDENTIFIER_FIELDS.some(
            (field) => Array.isArray(updatedValues[field]) && updatedValues[field].length > 0
        );
        if (isDateRangeLongerThanThreeMonths(values.fromDate, values.toDate) && !hasSearchIdentifier) {
            Modal.warning({
                title: intl.formatMessage({ id: 'missing-fields', defaultMessage: 'Missing fields' }),
                content: intl.formatMessage({
                    id: 'invoiceStatusSearchForm.longDateRangeIdentifierRequired',
                    defaultMessage: 'For date ranges longer than 3 months, please enter PartnerNo, Reference, VIN Number or UUID.',
                }),
            });
            return;
        }

        onSearch(updatedValues);
    };

    return (
        <Spin spinning={loading} size={'large'}>
            <div className="invoice-search-form">
                <Form {...formItemLayout} onFinish={handleFinish} form={form}>
                    <Row gutter={20}>
                        <Col span={24} style={{ paddingTop: 6 }}>
                            <div className="form-grid">
                                {/* Column 1: fromDate, toDate, status, reason */}
                                <div className="form-column">
                                    <div>
                                        <label>
                                            {intl.formatMessage({ id: 'invoiceStatusSearchForm.fromDate' })} *
                                        </label>
                                        <Form.Item
                                            name={'fromDate'}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: intl.formatMessage({
                                                        id: 'invoiceStatusSearchForm.fromDate_required',
                                                        defaultMessage: 'From Date is required',
                                                    }),
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={getDateFormatOrDefault(props.currentUser)}
                                                placeholder={intl.formatMessage({
                                                    id: 'invoiceStatusSearchForm.fromDateHint',
                                                })}
                                                allowClear={true}
                                                disabledDate={(current) => current.isAfter(dayjs())}
                                                onChange={handleFromDateChange}
                                                style={{ width: '100%' }}
                                                locale={getRangePickerLocalization(
                                                    intl,
                                                    localStorage.getItem('locale') || 'en'
                                                )}
                                            />
                                        </Form.Item>
                                    </div>

                                    <div>
                                        <label>{intl.formatMessage({ id: 'invoiceStatusSearchForm.toDate' })} *</label>
                                        <Form.Item
                                            name={'toDate'}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: intl.formatMessage({
                                                        id: 'invoiceStatusSearchForm.toDate_required',
                                                        defaultMessage: 'To Date is required',
                                                    }),
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={getDateFormatOrDefault(props.currentUser)}
                                                placeholder={intl.formatMessage({
                                                    id: 'invoiceStatusSearchForm.toDateHint',
                                                })}
                                                allowClear={true}
                                                onChange={handleToDate}
                                                disabledDate={(current) => current.isAfter(dayjs())}
                                                style={{ width: '100%' }}
                                                locale={getRangePickerLocalization(
                                                    intl,
                                                    localStorage.getItem('locale') || 'en'
                                                )}
                                            />
                                        </Form.Item>
                                    </div>

                                    <div>
                                        <label>{intl.formatMessage({ id: 'invoiceStatusSearchForm.status' })}</label>
                                        <Form.Item name={'status'}>
                                            <Select
                                                mode="multiple"
                                                allowClear={true}
                                                placeholder={intl.formatMessage({
                                                    id: 'invoiceStatusSearchForm.status',
                                                })}
                                            >
                                                {loading ? (
                                                    <Option key="searchCompCodes" value="">
                                                        ""
                                                    </Option>
                                                ) : (
                                                    removeFilterDuplicates(
                                                        filterOptions?.status ?? []
                                                    ).map((item) => (
                                                        <Option key={item.value} value={item.value}>
                                                            {item.value}
                                                        </Option>
                                                    ))
                                                )}
                                            </Select>
                                        </Form.Item>
                                    </div>

                                    <div>
                                        <label>{intl.formatMessage({ id: 'invoiceStatusSearchForm.reason' })}</label>
                                        <Form.Item name={'status_reason'}>
                                            <Input
                                                allowClear={true}
                                                placeholder={intl.formatMessage({
                                                    id: 'invoiceStatusSearchForm.reasonHint',
                                                })}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>

                                {/* Column 2: partnerNumber, company code */}
                                <div className="form-column">
                                    <div>
                                        <label>{intl.formatMessage({ id: 'invoiceStatusSearchForm.partnerNo' })}</label>
                                        <Form.Item name="partnerno">
                                            <Input
                                                allowClear={true}
                                                placeholder={intl.formatMessage({
                                                    id: 'invoiceStatusSearchForm.partnerNoHint',
                                                })}
                                            />
                                        </Form.Item>
                                    </div>

                                    <div>
                                        <label>{intl.formatMessage({ id: 'invoiceStatusSearchForm.company' })}</label>
                                        <Form.Item name={'company'}>
                                            <Select
                                                mode="multiple"
                                                allowClear={true}
                                                placeholder={intl.formatMessage({
                                                    id: 'invoiceStatusSearchForm.companyCodeHint',
                                                })}
                                            >
                                                {loading ? (
                                                    <Option key="searchCompCodes" value="">
                                                        ""
                                                    </Option>
                                                ) : (
                                                    data?.getAllMessagesFilters.company.map((item) => (
                                                        <Option key={item.value + item.text} value={item.value}>
                                                            {item.text}
                                                        </Option>
                                                    ))
                                                )}
                                            </Select>
                                        </Form.Item>
                                    </div>

                                    <div>
                                        <label>{intl.formatMessage({ id: 'ag-grid-group' })}</label>
                                        <Form.Item name={'group_name'}>
                                            <Select
                                                mode="multiple"
                                                allowClear={true}
                                                placeholder={intl.formatMessage({
                                                    id: 'ag-grid-group',
                                                })}
                                            >
                                                {loading ? (
                                                    <Option key="searchCompCodes" value="">
                                                        ""
                                                    </Option>
                                                ) : (
                                                    data?.getAllMessagesFilters.group_name
                                                        .filter((item) => item.value)
                                                        .map((item) => (
                                                            <Option key={`group-${item.value}`} value={item.value}>
                                                                {item.value}
                                                            </Option>
                                                        ))
                                                )}
                                            </Select>
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <label>
                                            {intl.formatMessage({
                                                id: 'document-available',
                                                defaultMessage: 'Document Available',
                                            })}
                                        </label>
                                        <Form.Item name={'document_available'}>
                                            <Select
                                                allowClear={true}
                                                placeholder={intl.formatMessage({
                                                    id: 'document-available',
                                                    defaultMessage: 'Document Available',
                                                })}
                                            >
                                                <Option key="available-yes" value="true">
                                                    Yes
                                                </Option>
                                                <Option key="available-no" value="false">
                                                    No
                                                </Option>
                                            </Select>
                                        </Form.Item>
                                    </div>
                                </div>

                                {/* Column 3: UUID, Reference */}
                                <div className="form-column">
                                    <div>
                                        <label>{intl.formatMessage({ id: 'invoiceStatusSearchForm.uuid' })}</label>
                                        <Form.Item name="message_id">
                                            <Input
                                                allowClear={true}
                                                placeholder={intl.formatMessage({
                                                    id: 'invoiceStatusSearchForm.uuidHint',
                                                })}
                                            />
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <label>{intl.formatMessage({ id: 'invoiceStatusSearchForm.reference' })}</label>
                                        <Form.Item name="reference">
                                            <Input
                                                allowClear={true}
                                                placeholder={intl.formatMessage({
                                                    id: 'invoiceStatusSearchForm.referenceHint',
                                                })}
                                            />
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <label>{intl.formatMessage({ id: 'grid-header-vin-number' })}</label>
                                        <Form.Item name="vin_number">
                                            <Input
                                                allowClear={true}
                                                placeholder={intl.formatMessage({ id: 'grid-header-vin-number' })}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>

                                {/* Column 4: process, interface */}
                                <div className="form-column">
                                    <div>
                                        <label>{intl.formatMessage({ id: 'invoiceStatusSearchForm.process' })}</label>
                                        <Form.Item name={'process'}>
                                            <Select
                                                mode="multiple"
                                                allowClear={true}
                                                placeholder={intl.formatMessage({
                                                    id: 'invoiceStatusSearchForm.processHint',
                                                })}
                                            >
                                                {loading ? (
                                                    <Option key="searchCompCodes" value="">
                                                        ""
                                                    </Option>
                                                ) : (
                                                    removeFilterDuplicates(
                                                        filterOptions?.process ?? []
                                                    ).map((item) => (
                                                        <Option key={item.value} value={item.value}>
                                                            {item.value}
                                                        </Option>
                                                    ))
                                                )}
                                            </Select>
                                        </Form.Item>
                                    </div>

                                    <div>
                                        <label>
                                            {intl.formatMessage({ id: 'invoiceStatusSearchForm.minterface' })}
                                        </label>
                                        <Form.Item name={'m_interface'}>
                                            <Select
                                                mode="multiple"
                                                allowClear={true}
                                                placeholder={intl.formatMessage({
                                                    id: 'invoiceStatusSearchForm.minterfaceHint',
                                                })}
                                            >
                                                {loading ? (
                                                    <Option key="searchCompCodes" value="">
                                                        ""
                                                    </Option>
                                                ) : (
                                                    (filterOptions?.m_interface || []).map((item) => (
                                                        <Option key={item.value} value={item.value}>
                                                            {item.value}
                                                        </Option>
                                                    ))
                                                )}
                                            </Select>
                                        </Form.Item>
                                    </div>

                                    <div>
                                        <label>{intl.formatMessage({ id: 'welcome-Format' })}</label>
                                        <Form.Item name={'m_format'}>
                                            <Select
                                                mode="multiple"
                                                allowClear={true}
                                                placeholder={intl.formatMessage({
                                                    id: 'welcome-Format',
                                                })}
                                            >
                                                {loading ? (
                                                    <Option key="minterface" value="">
                                                        ""
                                                    </Option>
                                                ) : (
                                                    removeFilterDuplicates(
                                                        filterOptions?.m_format ?? []
                                                    ).map((item, idx) => (
                                                        <Option key={`format-idx-${idx}`} value={item.value}>
                                                            {item.value}
                                                        </Option>
                                                    ))
                                                )}
                                            </Select>
                                        </Form.Item>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <div className="search-actions">
                        <Tooltip title={intl.formatMessage({ id: 'info-portal-search', defaultMessage: 'Search' })}>
                            <Button
                                className="search-actions__primary"
                                data-testid="info-portal-search-button"
                                type="primary"
                                htmlType="submit"
                                icon={<SearchOutlined />}
                                size={'large'}
                            >
                                {intl.formatMessage({ id: 'info-portal-search', defaultMessage: 'Search' })}{' '}
                            </Button>
                        </Tooltip>

                        <Tooltip title={intl.formatMessage({ id: 'do-clear', defaultMessage: 'Clear' })}>
                            <Button
                                data-testid="info-portal-clear"
                                type="default"
                                icon={<CloseOutlined />}
                                onClick={(_) => {
                                    form.resetFields();
                                    onReset();
                                }}
                                size={'large'}
                            >
                                {intl.formatMessage({ id: 'do-clear', defaultMessage: 'Clear' })}
                            </Button>
                        </Tooltip>
                    </div>
                </Form>
            </div>
        </Spin>
    );
};

export default InvoiceStatusSearchForm;
